<template>
  <button
    class="raw-body-3 rounded-full flex h-24 items-center gap-8 bg-[white]/40 p-2 ps-8 transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:bg-[color-mix(in_oklab,var(--raw-lightgrey)_75%,transparent)] active:bg-[color-mix(in_oklab,var(--raw-lightgrey)_75%,transparent)]"
    type="button"
    @click="handleShare"
  >
    <span class="line-clamp-1">
      {{ i18n(isCopied ? translations.copied : translations.share) }}
    </span>
    <component
      :is="isCopied ? IconCheckSmall : IconShare"
      class="rounded-full h-20 w-20 shrink-0 bg-[white] p-3"
    />
  </button>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { IconCheckSmall } from '@ds/icons/IconCheckSmall'
import { IconShare } from '@ds/icons/IconShare'

import translations from './ShareButton.translations'

const props = defineProps<{ zone: string }>()

const i18n = useI18n()
const { trackClick } = useTracking()

const isCopied = ref(false)
let copiedTimeout: ReturnType<typeof setTimeout> | undefined

function flagCopied() {
  isCopied.value = true
  if (copiedTimeout) {
    clearTimeout(copiedTimeout)
  }
  copiedTimeout = setTimeout(() => {
    isCopied.value = false
  }, 1000)
}

async function handleShare() {
  trackClick({ zone: props.zone, name: 'share', campaign: 'raw-materials' })

  const url = window.location.href

  // Native share sheet on touch devices only; desktop keeps the simpler
  // copy-to-clipboard. navigator.share needs a secure context (HTTPS).
  const isTouch = window.matchMedia('(pointer: coarse)').matches

  if (isTouch && typeof navigator.share === 'function') {
    try {
      await navigator.share({ url })

      return
    } catch (error) {
      // User dismissed the sheet: not an error, and no fallback wanted.
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }
      // Otherwise fall through to the clipboard fallback.
    }
  }

  try {
    await navigator.clipboard.writeText(url)
    flagCopied()
  } catch {
    // Clipboard unavailable or blocked (e.g. insecure context).
  }
}
</script>
